import { ReactNode } from 'react';
import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import { css } from '@emotion/css';
import { format, formatDistanceToNow } from 'date-fns';
import { ApiMetadata } from '../api/services';
import CopyableValue from './CopyableValue';

const STACKED_LAYOUT_QUERY = '@media (max-width: 560px)';
const ABSOLUTE_TIMESTAMP_FORMAT = "MMM d, yyyy 'at' h:mm a";

interface DetailRow {
  label: string;
  value: ReactNode;
}

function AuditValue({ timestamp, actor }: { timestamp?: number; actor?: string }) {
  const meridianTheme = useMeridianTheme();
  const secondaryClass = css({
    display: 'block',
    marginTop: 2,
    color: meridianTheme('themeInkSubdued'),
    fontSize: 13,
  });

  if (!timestamp) return <>{actor || '-'}</>;

  const secondaryParts = [formatDistanceToNow(timestamp, { addSuffix: true }), actor].filter(Boolean);
  return (
    <>
      <time dateTime={new Date(timestamp).toISOString()}>{format(timestamp, ABSOLUTE_TIMESTAMP_FORMAT)}</time>
      <span className={secondaryClass}>{secondaryParts.join(' · ')}</span>
    </>
  );
}

export default function ApiDetailsList({ api }: { api: ApiMetadata }) {
  const meridianTheme = useMeridianTheme();

  const rows: DetailRow[] = [
    { label: 'Service', value: <CopyableValue text={api.serviceName} /> },
    {
      label: 'Model package',
      value: api.modelPackageLocation ? <CopyableValue text={api.modelPackageLocation} /> : '-',
    },
    { label: 'Spec updated', value: <AuditValue timestamp={api.schemaUpdatedAt} /> },
    { label: 'Created', value: <AuditValue timestamp={api.createdAt} actor={api.createdBy} /> },
    { label: 'Modified', value: <AuditValue timestamp={api.modifiedAt} actor={api.modifiedBy} /> },
  ];

  const listClass = css({
    margin: 0,
    padding: '0 12px',
  });
  const rowClass = css({
    display: 'grid',
    gridTemplateColumns: '140px minmax(0, 1fr)',
    gap: 16,
    alignItems: 'baseline',
    padding: '14px 0',
    '& + &': {
      borderTop: `1px solid ${meridianTheme('themeBorderSubdued')}`,
    },
    [STACKED_LAYOUT_QUERY]: { gridTemplateColumns: 'minmax(0, 1fr)', gap: 4 },
  });
  const labelClass = css({
    margin: 0,
    color: meridianTheme('themeInkSubdued'),
    fontSize: 13,
    fontWeight: 500,
  });
  const valueClass = css({
    margin: 0,
    minWidth: 0,
    color: meridianTheme('themeInkDefault'),
    fontSize: 14,
    lineHeight: '20px',
  });

  return (
    <dl className={listClass}>
      {rows.map((row) => (
        <div key={row.label} className={rowClass}>
          <dt className={labelClass}>{row.label}</dt>
          <dd className={valueClass}>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}
